<template>
  <LcTable
    v-for="({ headline, prizeTiers }, index) in groupedPrizeTiers"
    :key="index"
    bordered
    :class="['tier-table', $attrs.class]"
  >
    <thead v-if="headline">
      <tr>
        <th colspan="2" scope="col">{{ headline }}</th>
      </tr>
    </thead>
    <thead v-else-if="isPickLottery">
      <tr>
        <th scope="col">
          {{ $t('tiertable.header.playtype') }}
        </th>
        <th scope="col">
          {{ $t('tiertable.header.prizeamount') }}
        </th>
      </tr>
    </thead>

    <tbody>
      <tr v-for="prizeTier in getPreparedPrizeTiers(prizeTiers)" :key="prizeTier.tier">
        <td>
          <template v-if="isNumberLottery">
            <TierTableLineItem
              v-bind="{ addOns, licensedTerritory, lotteryId }"
              :fields="getFields(prizeTier.requirements)"
            />
          </template>
          <template v-else-if="isPickLottery || isDateLottery">
            {{ getPerPlayTranslation(lotteryId, 'betType', prizeTier.tier) }}
          </template>
        </td>

        <td>
          <template v-if="type === 'winners'">
            <div>{{ prizeTier.winners ?? 0 }}</div>
          </template>
          <template v-else>
            <template v-if="!prizeTier.prize?.value && prizeTier.type === PrizeTierType.Jackpot">
              {{ $t('tiertable.jackpot') }}
            </template>
            <template v-else-if="prizeTier.type === PrizeTierType.FreeTicket">
              <div class="text-accent-accent-success text-uppercase">
                {{ $t('tiertable.freeticket') }}
              </div>
            </template>
            <template v-else>
              <div>{{ prizeTier.formattedPrize }}</div>

              <div v-if="prizeTier.payoutInterval !== PayoutInterval.Once">
                {{
                  getTranslation({
                    key: `tiertable.payoutinterval.${prizeTier.payoutInterval}`,
                  })
                }}
              </div>
            </template>
          </template>
        </td>
      </tr>
    </tbody>
  </LcTable>
</template>

<script lang="ts" setup>
import type { LcLotteryNumber } from '@lottocom/frontend-components'

import {
  type AddOn,
  type Money as MoneyType,
  type NumberLottery,
  type NumberRequirement,
  PayoutInterval,
  type PrizeTierRequirements,
  PrizeTierType,
} from '#api/shared/@types/generated/backend/graphql-schema-types'

import type {
  LineItemField,
  LineItemProps,
} from '../../../../../app/components/LineItem/LineItem.vue'

import { NO_MATCH_FIELD_NAME } from './TierTableLineItem.vue'

export type TierTableGroupedPrizeTier = {
  headline?: Maybe<string>
  prizeTiers: TierTablePrizeTier[]
}

export type TierTablePrizeTier = {
  payoutInterval: PayoutInterval
  prize?: Maybe<MoneyType>
  requirements: PrizeTierRequirements
  tier: string
  type: PrizeTierType
  winners?: Maybe<number>
}

export type TierTableProps = Pick<LineItemProps, 'licensedTerritory' | 'lotteryId'> & {
  addOns: AddOn[]
  groupedPrizeTiers: TierTableGroupedPrizeTier[]
  type: TierTableType
}

export type TierTableType = 'prizes' | 'winners'

type PreparedPrizeTier = TierTablePrizeTier & { formattedPrize: string }

const GROUPABLE_TIERS = ['pair', 'wheel']

const isNoMatch = ({ amountOfCorrectNumbers }: NumberRequirement) => !amountOfCorrectNumbers

const hasNumberLotteryRequirements = (
  requirements: PrizeTierRequirements,
): requirements is NumberLottery => !!(requirements as NumberLottery).numberRequirements

const { lotteryId } = defineProps<TierTableProps>()

const { getTranslation } = useI18nUtils()
const { getPerPlayTranslation } = useTranslations()
const { formatPrizeMoney } = usePrizeMoneyFormatter()

const { isDateLottery, isNumberLottery, isPickLottery } = useLotteryType(lotteryId)

const getFields = (requirements: PrizeTierRequirements) => {
  if (!hasNumberLotteryRequirements(requirements)) return []

  const { numberRequirements } = requirements
  if (numberRequirements.every(isNoMatch)) {
    return [
      {
        name: NO_MATCH_FIELD_NAME,
        numbers: [{ value: '' }],
      },
    ]
  }

  return numberRequirements.map<LineItemField>(({ amountOfCorrectNumbers, numbersReference }) => ({
    name: numbersReference,
    numbers: new Array<LcLotteryNumber>(amountOfCorrectNumbers).fill({
      value: getTranslation({
        key: `tiertable.ball.${numbersReference}.${lotteryId}`,
      }),
    }),
  }))
}

const getPreparedPrizeTiers = (prizeTiers: TierTablePrizeTier[]) => {
  const preparedTiers = prizeTiers.map((prizeTier) => ({
    ...prizeTier,
    formattedPrize: formatPrizeMoney(prizeTier.prize),
  }))

  if (isNumberLottery.value || isDateLottery.value) return preparedTiers

  const groupedTiers: PreparedPrizeTier[] = []

  preparedTiers.forEach((tier) => {
    const tierGroup = GROUPABLE_TIERS.find((groupableTier) => tier.tier.includes(groupableTier))

    if (!tierGroup) {
      groupedTiers.push(tier)
      return
    }

    if (groupedTiers.some(({ tier }) => tier === tierGroup)) return

    groupedTiers.push({
      ...tier,
      tier: tierGroup,
    })
  })

  return groupedTiers
}
</script>

<style lang="scss" scoped>
.lc-table {
  td:nth-child(n + 2):last-child,
  th:nth-child(n + 2):last-child {
    text-align: right;
  }

  td:nth-child(n + 2):last-child {
    @include token-paragraph-bold;
  }
}
</style>
